import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { createEditor, Descendant, BaseEditor, Transforms, Element as SlateElement, Editor } from 'slate';
import { withReact, ReactEditor } from 'slate-react';
import { withHistory, HistoryEditor } from 'slate-history';

// Define the shape of our Slate elements
type CustomElement = {
  type: 'paragraph' | 'heading-one' | 'heading-two' | 'bulleted-list' | 'numbered-list' | 'list-item';
  children: CustomText[];
  align?: 'left' | 'center' | 'right' | 'justify';
};

type CustomText = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  fontSize?: number; // Font size in points
};

declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor & HistoryEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

// Initial value for the editor - ZAVA Insights Report
const initialValue: Descendant[] = [
  {
    type: 'heading-one',
    children: [{ text: 'The Global Smart Living Fixtures Opportunity (2026–2030)' }],
  },
  {
    type: 'paragraph',
    children: [
      { text: 'Published: January 2026', italic: true },
    ],
  },
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
  {
    type: 'paragraph',
    children: [
      { text: 'Smart furniture has spent most of the past decade living in the shadow of the "smart home" story. Consumers broadly like connected lighting, cameras, speakers, and thermostats, but they have been far less consistent in buying connected ' },
      { text: 'furniture', italic: true },
      { text: '—especially the big, heavy, high-expectation items that must look good, last for years, and get installed without drama. That gap is exactly where ZAVA positions itself: not as a gadget brand, but as an AI-first, design-led home brand that bundles hardware, software, and services into products people already budget for when they move, remodel, or upgrade their homes.' },
    ],
  },
  {
    type: 'paragraph',
    children: [
      { text: 'This report frames ZAVA\'s category as ' },
      { text: 'smart living fixtures', bold: true },
      { text: '—a practical cluster that includes smart seating (e.g., sofas), smart entry systems (doors/locks/access), and smart mirrors—because that is how customers actually buy: as part of renovation projects, interior upgrades, or security improvements. The industry\'s formal taxonomies often split these products across different buckets, but the consumer wallet tends to unify them into "home improvement."' },
    ],
  },
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
  {
    type: 'heading-two',
    children: [{ text: 'The market is enormous—and the label "smart furniture" understates it' }],
  },
  {
    type: 'paragraph',
    children: [
      { text: 'If you start from the headline smart-home numbers, the opportunity looks almost too big to fail. Grand View Research estimates the global smart home market at ' },
      { text: 'USD 127.80 billion in 2024', bold: true },
      { text: ', projecting it to reach ' },
      { text: 'USD 537.27 billion by 2030', bold: true },
      { text: '. That growth is not just a marketing phenomenon; it is supported by a very large installed base and a continuous flow of device shipments. IDC, for example, reported that worldwide shipments of smart home devices were expected to be essentially flat in 2024 at ' },
      { text: '892.3 million units', bold: true },
      { text: ', after a weak cycle, with growth expected to resume as emerging markets and new use-cases expand the base.' },
    ],
  },
  {
    type: 'paragraph',
    children: [
      { text: 'But when you zoom into "smart furniture" as a narrow, formally-defined market, the numbers shrink dramatically. Grand View Research estimates global smart furniture at ' },
      { text: 'USD 218.5 million in 2024', bold: true },
      { text: ', reaching ' },
      { text: 'USD 486.2 million by 2030', bold: true },
      { text: '. That is not because consumers don\'t spend on furniture—it\'s because the "smart" value is frequently counted elsewhere (smart security, displays, home platforms, appliances), and because many furniture upgrades still occur as analog purchases routed through design and construction workflows.' },
    ],
  },
  {
    type: 'paragraph',
    children: [
      { text: 'To see the more realistic commercial cluster for ZAVA, it\'s more useful to look at adjacent categories that are already scaled. Smart locks, which sit at the intersection of security and access convenience, are estimated at ' },
      { text: 'USD 2,770.1 million in 2024', bold: true },
      { text: ', projected to reach ' },
      { text: 'USD 8,136.9 million by 2030', bold: true },
      { text: '. Smart mirrors are a perfect example of taxonomy confusion: one definition (focused on retail and home smart mirrors) puts the market at ' },
      { text: 'USD 514.6 million in 2022', bold: true },
      { text: ' and ' },
      { text: 'USD 1,012.6 million by 2030', bold: true },
      { text: '. Another common definition includes broader use-cases such as automotive and other display mirror applications and reports a much larger market, such as ' },
      { text: 'USD 3.3 billion in 2024', bold: true },
      { text: ' with longer-horizon growth. For ZAVA\'s planning, the "home fixture" view matters most, but the definitional spread is a reminder that this space is still being shaped by how analysts count it.' },
    ],
  },
  {
    type: 'paragraph',
    children: [
      { text: 'The punchline is that ZAVA should not anchor its ambition to the narrow "smart furniture" line item. The far more important question is: how much of the mainstream furniture and renovation spend can be converted into "smart-upgraded" purchases when the experience is trusted, beautiful, and painless?' },
    ],
  },
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
  {
    type: 'heading-two',
    children: [{ text: 'Home investment cycles—not gadget enthusiasm—are the real engine' }],
  },
  {
    type: 'paragraph',
    children: [
      { text: 'The reason smart fixtures can scale is that they attach to much larger, durable spending cycles. Residential remodeling is estimated at ' },
      { text: 'USD 2.70 trillion in 2023', bold: true },
      { text: ' and projected to reach ' },
      { text: 'USD 3.67 trillion by 2030', bold: true },
      { text: '. At the same time, Deloitte\'s Global Powers of Construction reporting highlights a broad growth arc in global construction, projecting the market to expand from ' },
      { text: 'USD 11.39 trillion in 2024', bold: true },
      { text: ' to ' },
      { text: 'USD 16.11 trillion by 2030', bold: true },
      { text: '. These are the budgets and timelines where doors get replaced, entry systems get upgraded, bathrooms get rebuilt, and living rooms get redesigned. In other words, the moment when consumers are already paying for labor, measurement, delivery, and installation is the moment when "smart" becomes easiest to justify—provided it doesn\'t add risk.' },
    ],
  },
  {
    type: 'paragraph',
    children: [
      { text: 'This is also why the "smart furniture is tiny" critique can be misleading. Furniture itself is a massive global category; Grand View Research estimates the global furniture market at ' },
      { text: 'USD 745.65 billion in 2024', bold: true },
      { text: ', projecting it to reach ' },
      { text: 'USD 1,334.08 billion by 2033', bold: true },
      { text: '. Even a small conversion rate from traditional furniture and fixtures into smart-enabled equivalents can build a very large business.' },
    ],
  },
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
  {
    type: 'heading-two',
    children: [{ text: 'Why 2026 is a timing window: interoperability and access are finally standardizing' }],
  },
  {
    type: 'paragraph',
    children: [
      { text: 'Smart home adoption has always been constrained by friction: incompatible ecosystems, setup fatigue, and the fear that a purchase will break when a phone changes. The most meaningful shift in the mid-2020s is the industry\'s march toward interoperability and standardized onboarding. Matter is widely positioned as a unifying, IP-based protocol intended to help devices work reliably across ecosystems. Independent technical literature has similarly described Matter as a royalty-free connectivity standard designed to unify smart-home devices and improve compatibility across platforms.' },
    ],
  },
  {
    type: 'paragraph',
    children: [
      { text: 'For ZAVA\'s "smart entry" business, the access layer is becoming even more important. In early January 2026, reporting noted that ' },
      { text: 'Aliro', bold: true },
      { text: '—an open standard for smart locks and digital keys—was set to launch in 2026, aiming to standardize how locks communicate with phones and watches regardless of platform. The Connectivity Standards Alliance (the organization behind Matter) presents Aliro as a standardized communication protocol between access readers and user devices. In practical terms, Aliro matters because it reduces the cross-platform "key chaos" that makes smart access frustrating in mixed-OS households and multi-user environments.' },
    ],
  },
  {
    type: 'paragraph',
    children: [
      { text: 'The net effect is that the next wave of smart fixtures can feel less like "devices you manage" and more like "home infrastructure that works." That transition is exactly the precondition for ZAVA\'s positioning as an AI-first home brand rather than a gadget catalog.' },
    ],
  },
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
  {
    type: 'heading-two',
    children: [{ text: 'The competitive battlefield is real—but it is fragmented in a way ZAVA can exploit' }],
  },
  {
    type: 'paragraph',
    children: [
      { text: 'The most durable competitors in this space do not look identical, and that fragmentation is an opportunity. Consider a Scandinavian flat-pack design retailer like ' },
      { text: 'NørHome', bold: true },
      { text: ': it has supply-chain gravity, showroom footprints, and design credibility, but it historically monetizes breadth and price more than deep tech and service reliability. Contrast that with platform-centric ecosystems such as ' },
      { text: 'CloudNest', bold: true },
      { text: ' or ' },
      { text: 'OrionThings', bold: true },
      { text: ', which excel at software distribution and onboarding but often struggle to deliver furniture-grade aesthetics, long-lived hardware, and high-touch installation experiences. Then there are legacy access-control players—names like ' },
      { text: 'SecureLatch Group', bold: true },
      { text: ' in this report\'s shorthand—whose products are trusted for reliability and installer familiarity, but whose consumer storytelling, cross-sell design, and cohesive app experiences lag behind what modern buyers expect.' },
    ],
  },
  {
    type: 'paragraph',
    children: [
      { text: 'ZAVA\'s advantage is not that it can outspend these archetypes in their strongholds. It\'s that ZAVA can build a coherent value proposition across the messy seams: design that belongs in a living room, installation that feels predictable, privacy that feels respectful, and interoperability that reduces buyer anxiety. In categories like doors/locks and mirrors—where installation and trust dominate—this is often the difference between a premium brand and a high-return-rate gadget.' },
    ],
  },
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
  {
    type: 'heading-two',
    children: [{ text: 'A realistic ZAVA positioning: "the AI-first home, delivered as a finished project"' }],
  },
  {
    type: 'paragraph',
    children: [
      { text: 'For a brand like ZAVA, the most believable strategy is to present smart fixtures as part of a finished home outcome rather than as boxes shipped to a doorstep. In practice, that means the product is only half the experience; the other half is measurement, installation, commissioning, and long-term support. When this is done well, it doesn\'t just reduce support tickets—it changes the buyer\'s willingness to pay, because the purchase feels less risky.' },
    ],
  },
  {
    type: 'paragraph',
    children: [
      { text: 'In ZAVA\'s category cluster, three product pillars are commercially coherent. Smart seating becomes the "daily delight" product—comfort, posture, and routines that users feel every day. Smart entry becomes the "trust anchor"—security, access management, and reliability, which also opens doors (literally) into partnerships with developers, property managers, and hospitality. Smart mirrors become the "routine interface"—a place for lighting, presence, and opt-in assistance that can be useful without being invasive.' },
    ],
  },
  {
    type: 'paragraph',
    children: [
      { text: 'The technology roadmap should be standards-forward. Matter alignment reduces ecosystem risk and improves customer confidence. Aliro readiness is likely to become a critical checklist item for access products as the standard reaches market and device support expands. In a world where consumers already own a pile of smart devices, the brand that integrates cleanly will often beat the brand that simply has more features.' },
    ],
  },
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
  {
    type: 'heading-two',
    children: [{ text: 'Outlook model: what a credible growth path can look like (2026–2030)' }],
  },
  {
    type: 'paragraph',
    children: [
      { text: 'To keep the forecast grounded, ZAVA\'s growth is modeled around three measurable levers: distribution footprint (showrooms and partner installers), conversion (project-to-purchase), and attach (services and optional ongoing coverage). The goal is not to claim a single "true" number; it is to present a set of scenarios that allow leadership to calibrate assumptions against real-world traction.' },
    ],
  },
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
];

// Editor commands interface that Word ribbon will use
interface WordSlateCommands {
  // Formatting commands
  toggleBold: () => void;
  toggleItalic: () => void;
  toggleUnderline: () => void;
  toggleStrikethrough: () => void;
  
  // Alignment commands
  toggleAlignLeft: () => void;
  toggleAlignCenter: () => void;
  toggleAlignRight: () => void;
  toggleAlignJustify: () => void;
  
  // Font size commands
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  
  // List commands
  toggleBulletedList: () => void;
  toggleNumberedList: () => void;
  
  // Indent commands
  increaseIndent: () => void;
  decreaseIndent: () => void;
  
  // History commands
  undo: () => void;
  redo: () => void;
  
  // State checking commands (for button active states)
  isBoldActive: () => boolean;
  isItalicActive: () => boolean;
  isUnderlineActive: () => boolean;
  isStrikethroughActive: () => boolean;
  isBulletedListActive: () => boolean;
  isNumberedListActive: () => boolean;
  getAlignment: () => 'left' | 'center' | 'right' | 'justify' | undefined;
  
  // Document content commands
  getDocumentText: () => string;
  
  // Utility commands
  focus: () => void;
  blur: () => void;
}

interface WordSlateEditorContextType {
  editor: Editor;
  value: Descendant[];
  setValue: (value: Descendant[]) => void;
  commands: WordSlateCommands;
  version: number; // Version counter to trigger re-renders
}

const WordSlateEditorContext = createContext<WordSlateEditorContextType | undefined>(undefined);

// Helper functions for checking and toggling marks
const isMarkActive = (editor: Editor, format: keyof Omit<CustomText, 'text'>) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

const toggleMark = (editor: Editor, format: keyof Omit<CustomText, 'text'>) => {
  console.log(`toggleMark called with format: ${format}`);
  const isActive = isMarkActive(editor, format);
  console.log(`Mark ${format} is currently active: ${isActive}`);

  // Ensure the editor is focused
  ReactEditor.focus(editor);
  
  // Apply the mark regardless of selection state
  if (isActive) {
    console.log(`Removing mark: ${format}`);
    Editor.removeMark(editor, format);
  } else {
    console.log(`Adding mark: ${format}`);
    Editor.addMark(editor, format, true);
  }
  console.log('toggleMark completed');
};// Helper functions for block-level operations
const isBlockActive = (editor: Editor, format: string) => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: n =>
        !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
    })
  );

  return !!match;
};

const getBlockAlignment = (editor: Editor): 'left' | 'center' | 'right' | 'justify' | undefined => {
  const { selection } = editor;
  if (!selection) return undefined;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: n => !Editor.isEditor(n) && SlateElement.isElement(n),
    })
  );

  if (match && SlateElement.isElement(match[0])) {
    return match[0].align;
  }
  
  return undefined;
};

type ElementType = 'paragraph' | 'heading-one' | 'heading-two' | 'bulleted-list' | 'numbered-list' | 'list-item';

const toggleBlock = (editor: Editor, format: string) => {
  const isActive = isBlockActive(editor, format);
  const isList = ['numbered-list', 'bulleted-list'].includes(format);

  Transforms.unwrapNodes(editor, {
    match: n =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      ['numbered-list', 'bulleted-list'].includes(n.type),
    split: true,
  });

  const newType: ElementType = isActive ? 'paragraph' : isList ? 'list-item' : (format as ElementType);
  const newProperties: Partial<SlateElement> = {
    type: newType,
  };

  Transforms.setNodes<SlateElement>(editor, newProperties);

  if (!isActive && isList) {
    const block: SlateElement = { type: format as ElementType, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

const setAlignment = (editor: Editor, alignment: 'left' | 'center' | 'right' | 'justify') => {
  Transforms.setNodes<SlateElement>(
    editor,
    { align: alignment },
    { match: n => !Editor.isEditor(n) && SlateElement.isElement(n) }
  );
};

// Helper functions for font size
const DEFAULT_FONT_SIZE = 11; // Default Word font size in points

const getCurrentFontSize = (editor: Editor): number => {
  const marks = Editor.marks(editor);
  return marks?.fontSize ?? DEFAULT_FONT_SIZE;
};

const setFontSize = (editor: Editor, fontSize: number) => {
  // Ensure fontSize is within reasonable bounds (8-72 points)
  const clampedSize = Math.max(8, Math.min(72, fontSize));
  
  // Ensure the editor is focused
  ReactEditor.focus(editor);
  
  // Set the font size mark
  Editor.addMark(editor, 'fontSize', clampedSize);
};

export const WordSlateEditorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Create editor with React and History plugins
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);
  
  // Editor value state
  const [value, setValue] = useState<Descendant[]>(initialValue);
  
  // Version counter to trigger re-renders when selection changes
  const [version, setVersion] = useState(0);
  
  // Wrap setValue to also increment version
  const handleSetValue = useCallback((newValue: Descendant[]) => {
    setValue(newValue);
    setVersion(v => v + 1);
  }, []);

  // Commands that will be exposed to the Word ribbon
  const commands: WordSlateCommands = useMemo(() => ({
    // Formatting commands
    toggleBold: () => {
      console.log('toggleBold command called');
      toggleMark(editor, 'bold');
    },
    toggleItalic: () => {
      console.log('toggleItalic command called');
      toggleMark(editor, 'italic');
    },
    toggleUnderline: () => toggleMark(editor, 'underline'),
    toggleStrikethrough: () => toggleMark(editor, 'strikethrough'),
    
    // Alignment commands
    toggleAlignLeft: () => setAlignment(editor, 'left'),
    toggleAlignCenter: () => setAlignment(editor, 'center'),
    toggleAlignRight: () => setAlignment(editor, 'right'),
    toggleAlignJustify: () => setAlignment(editor, 'justify'),
    
    // Font size commands (simplified - could be enhanced with actual font size tracking)
    increaseFontSize: () => {
      const currentSize = getCurrentFontSize(editor);
      setFontSize(editor, currentSize + 1);
    },
    decreaseFontSize: () => {
      const currentSize = getCurrentFontSize(editor);
      setFontSize(editor, currentSize - 1);
    },
    
    // List commands
    toggleBulletedList: () => toggleBlock(editor, 'bulleted-list'),
    toggleNumberedList: () => toggleBlock(editor, 'numbered-list'),
    
    // Indent commands (simplified - could be enhanced with custom indentation logic)
    increaseIndent: () => {
      console.log('Increase indent - TODO: implement with custom logic');
    },
    decreaseIndent: () => {
      console.log('Decrease indent - TODO: implement with custom logic');
    },
    
    // History commands
    undo: () => editor.undo(),
    redo: () => editor.redo(),
    
    // State checking commands (for button active states)
    isBoldActive: () => isMarkActive(editor, 'bold'),
    isItalicActive: () => isMarkActive(editor, 'italic'),
    isUnderlineActive: () => isMarkActive(editor, 'underline'),
    isStrikethroughActive: () => isMarkActive(editor, 'strikethrough'),
    isBulletedListActive: () => isBlockActive(editor, 'bulleted-list'),
    isNumberedListActive: () => isBlockActive(editor, 'numbered-list'),
    getAlignment: () => getBlockAlignment(editor),
    
    // Document content commands
    getDocumentText: () => {
      // Extract all text from the document
      const extractText = (nodes: Descendant[]): string => {
        return nodes.map(node => {
          if ('text' in node) {
            return node.text;
          }
          if ('children' in node) {
            return extractText(node.children as Descendant[]);
          }
          return '';
        }).join(' ');
      };
      return extractText(value).trim();
    },
    
    // Utility commands
    focus: () => {
      Transforms.select(editor, Editor.end(editor, []));
      // ReactEditor.focus will be handled by the Editable component
    },
    blur: () => {
      // ReactEditor.blur will be handled by the Editable component
    },
  }), [editor, value]);

  const contextValue: WordSlateEditorContextType = useMemo(() => ({
    editor,
    value,
    setValue: handleSetValue,
    commands,
    version,
  }), [editor, value, commands, handleSetValue, version]);

  return (
    <WordSlateEditorContext.Provider value={contextValue}>
      {children}
    </WordSlateEditorContext.Provider>
  );
};

// Hook to use the Word Slate editor context
export const useWordSlateEditor = () => {
  console.log('useWordSlateEditor called');
  const context = useContext(WordSlateEditorContext);
  console.log('useWordSlateEditor context:', context);
  if (context === undefined) {
    console.error('useWordSlateEditor must be used within a WordSlateEditorProvider');
    throw new Error('useWordSlateEditor must be used within a WordSlateEditorProvider');
  }
  return context;
};

// Hook that provides commands compatible with existing Word ribbon interface
export const useWordEditorCommands = () => {
  console.log('useWordEditorCommands called');
  const { commands } = useWordSlateEditor();
  console.log('useWordEditorCommands commands:', commands);
  return commands;
};